import { Check, TriangleAlert } from 'lucide-react';
import { useDosingWeight, useRenal, useSettings } from '@/store/selectors';
import { EIEligibility, RenalStability, WeightRule } from '@/types';
import { renalStabilityLabel } from '@/utils/format';

/** Derived pharmacokinetic values: IBW/AdjBW/selected weight, CrCl, eGFR (PSD §8.1.2 / TODO Task 1.24). */
export function DerivedValuesPanel(): JSX.Element {
  const dw = useDosingWeight();
  const renal = useRenal();
  const settings = useSettings();

  if (!dw || !renal) {
    return (
      <section className="panel" aria-label="Derived values" data-testid="derived-values">
        <h2 className="panel-title">Derived Values</h2>
        <div className="empty-state">Complete patient inputs to see derived values, or use <strong>Load Demo</strong> (top right) for a worked example.</div>
      </section>
    );
  }

  const eiLabel =
    renal.eiEligibility === EIEligibility.Eligible
      ? 'Eligible'
      : renal.eiEligibility === EIEligibility.Borderline
        ? `Borderline (${renal.borderlineBandMlMin} mL/min band)`
        : 'Ineligible';

  return (
    <section className="panel panel-result" aria-label="Derived values" data-testid="derived-values">
      <h2 className="panel-title">Derived Values</h2>
      <div className="value-row">
        <span className="value-label">IBW (Devine)</span>
        <span className="value-num">{dw.ibwKg.toFixed(1)} kg</span>
      </div>
      {dw.selectionRule === WeightRule.UseAdjBW && (
        <div className="value-row">
          <span className="value-label">AdjBW (40% rule)</span>
          <span className="value-num">{dw.adjbwKg.toFixed(1)} kg</span>
        </div>
      )}
      <div className="value-row">
        <span className="value-label">Actual BW</span>
        <span className="value-num">{dw.abwKg.toFixed(1)} kg</span>
      </div>
      <div className="value-row highlight" title="Weight used for all mg/kg dosing. ABW if < IBW; IBW if ≤ 120% IBW; AdjBW if > 120% IBW.">
        <span className="value-label">Dosing weight ({dw.selectionRule})</span>
        <span className="value-num">{dw.selectedWeightKg.toFixed(1)} kg</span>
      </div>
      {dw.override && (
        <div className="value-row">
          <span className="value-label">Override</span>
          <span className="value-num text-warn">
            {dw.overrideWeightKg?.toFixed(1)} kg — {dw.overrideReason}
          </span>
        </div>
      )}
      <hr className="divider" />
      <div className="value-row" title="Cockcroft-Gault creatinine clearance. Primary renal function estimate for aminoglycoside dosing.">
        <span className="value-label">CrCl (Cockcroft-Gault)</span>
        <span className="value-num">{renal.crClMlMin.toFixed(1)} mL/min</span>
      </div>
      {renal.egfrMlMin173 != null && (
        <div className="value-row" title="CKD-EPI 2021 race-free eGFR. Cross-check for CrCl — divergence > 30% is flagged.">
          <span className="value-label">eGFR (CKD-EPI)</span>
          <span className="value-num">{renal.egfrMlMin173.toFixed(1)} mL/min/1.73</span>
        </div>
      )}
      {renal.egfrDiscrepancyNote && (
        <div className="hint-text">{renal.egfrDiscrepancyNote}</div>
      )}
      <div className="value-row" title="Extended-interval eligibility based on CrCl vs institutional renal cutoff.">
        <span className="value-label">EI eligibility</span>
        <span className={`badge ${renal.eiEligibility === EIEligibility.Eligible ? 'badge-safe' : 'badge-warn'}`}>
          {renal.eiEligibility === EIEligibility.Eligible
            ? <Check size={12} strokeWidth={2.5} aria-hidden="true" />
            : <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />}
          {eiLabel}
        </span>
      </div>
      <div className="value-row" title="Population elimination rate constant: kₑl = 0.0024 × CrCl + 0.01 hr⁻¹.">
        <span className="value-label">Pop. k<sub>el</sub></span>
        <span className="value-num">{renal.populationKelPerHr.toFixed(4)} hr⁻¹</span>
      </div>
      {!settings.educationalMode && renal.stability !== RenalStability.Stable && (
        <div className="badge badge-warn">
          <TriangleAlert size={12} strokeWidth={2} aria-hidden="true" />
          Renal function: {renalStabilityLabel(renal.stability)}
        </div>
      )}
    </section>
  );
}
